import React, { FC } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend, ResponsiveContainer, LabelList } from 'recharts';
import { AnalyticsChartColors, AnalyticsChartColorsArray } from '../../enums/analytics-chart-colors.enum';
import { EngagementStatusByLanguageChartRow } from './engagement-status-by-language-chart.component';

export type EngagementStatusBarChartComponentProps = {
	data?: EngagementStatusByLanguageChartRow[];
};

export const EngagementStatusBarChartComponent: FC<EngagementStatusBarChartComponentProps> = (props) => {
	const { data } = props;
	const appTheme = useTheme();
	const STACK_KEYS = ['Completed', 'In Progress', 'New', 'Other'];

	const getTopKey = (row: any) => {
		for (let i = STACK_KEYS.length - 1; i >= 0; i--) {
			if (row[STACK_KEYS[i]] > 0) return STACK_KEYS[i];
		}
		return null;
	};

	const RoundedShape = (props: any) => {
		const { x, y, width, height, fill, payload, dataKey } = props;
		const topKey = getTopKey(payload);
		const isTop = topKey === dataKey;

		const r = isTop ? 10 : 0;

		return (
			<path
				d={`
        M${x},${y + height}
        L${x},${y + r}
        Q${x},${y} ${x + r},${y}
        L${x + width - r},${y}
        Q${x + width},${y} ${x + width},${y + r}
        L${x + width},${y + height}
        Z
      `}
				fill={fill}
			/>
		);
	};

	const renderPercentLabel = (props: any) => {
		const { x, y, width, height, value } = props;

		if (!value || value < 5) return null;

		return (
			<text
				x={x + width / 2}
				y={y + height / 2}
				fill="#000"
				textAnchor="middle"
				dominantBaseline="central"
				fontSize={12}
				fontWeight={400}
			>
				{`${Math.round(value)}%`}
			</text>
		);
	};

	const CustomLegend = (props: any) => (
		<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '10px', mb: 2 }}>
			{props.payload.map((entry: any) => (
				<Box key={entry.value} style={{ color: entry.color }}>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
						<Box
							sx={{
								height: 10,
								width: 10,
								borderRadius: '50%',
								backgroundColor: entry.color,
								flexShrink: 0,
							}}
						></Box>
						<Typography data-overflow="true" variant="body2" sx={{ color: '#797F92', fontSize: '14px' }}>
							{entry.value}
						</Typography>
					</Box>
				</Box>
			))}
		</Box>
	);

	return (
		<ResponsiveContainer width="100%" height="100%">
			<BarChart
				data={data && data.length > 0 ? data : [{ language: '', Completed: 0, 'In Progress': 0, New: 0, Other: 0 }]}
				margin={{ right: 14, left: 14 }}
				barCategoryGap="12%"
			>
				<CartesianGrid strokeDasharray="3 3" syncWithTicks={true} />
				<XAxis
					dataKey="language"
					tickSize={12}
					tick={({ x, y, payload, textAnchor, ...rest }) => (
						<text x={x} y={y + 12} orientation={'bottom'} textAnchor={'middle'} {...rest}>
							<tspan fontSize={12}>{payload.value}</tspan>
						</text>
					)}
				/>
				<YAxis
					allowDecimals={true}
					tickSize={12}
					domain={[0, 100]}
					ticks={[0, 25, 50, 75, 100]}
					tick={({ x, y, payload, textAnchor, ...rest }) => (
						<text x={x} y={y + 5} textAnchor={'end'} {...rest} fill={appTheme.colors.grayLightBlue} fontWeight={500}>
							<tspan fontSize={12}>{`${payload.value}%`}</tspan>
						</text>
					)}
				/>
				<Legend verticalAlign="top" align="right" content={<CustomLegend />} />
				<Bar
					dataKey="Completed"
					stackId="total"
					fill={AnalyticsChartColors.Sky}
					shape={<RoundedShape />}
					maxBarSize={120}
				>
					<LabelList dataKey="Completed" content={renderPercentLabel} />
				</Bar>
				<Bar
					dataKey="In Progress"
					stackId="total"
					fill={AnalyticsChartColors.SeaGreen}
					shape={<RoundedShape />}
					maxBarSize={120}
				>
					<LabelList dataKey="In Progress" content={renderPercentLabel} />
				</Bar>
				<Bar dataKey="New" stackId="total" fill={AnalyticsChartColors.Yellow} shape={<RoundedShape />} maxBarSize={120}>
					<LabelList dataKey="New" content={renderPercentLabel} />
				</Bar>
				<Bar dataKey="Other" stackId="total" fill={AnalyticsChartColors.Grey} shape={<RoundedShape />} maxBarSize={120}>
					<LabelList dataKey="Other" content={renderPercentLabel} />
				</Bar>
			</BarChart>
		</ResponsiveContainer>
	);
};
